// PickerList — the INSIDE of every picker (ADR-080 §2 · §6 · §11 · §14), props
// in, events out: the search box (past 8, or a server kind), the START LIST of
// a long list ("Selected" on top at rest — the rows chosen when the picker
// opened; the results with the ticks in place once typed), the suggestion
// GROUPS with their headings, the rows, the notices, and the count READ ALOUD
// (a polite status line — nothing visible, architect-ruled). Hosted by `PickerField`'s desk panel and phone sheet, the
// filter kit's drill, chip popover and drawer, and the Add dialogs later —
// one behaviour lands here for every host. The rows come from `useOptions`
// (the three kinds), already matched and ordered; this primitive never
// fetches and never matches.
//
// The React Aria parts (verified on the installed build): a FLAT list is
// `Autocomplete` + `ListBox` (the search box drives the list with virtual
// focus — ArrowDown moves through the rows while typing continues; sections
// carry the groups' headings). A list holding a GROUP ROW (a zone › its
// clusters, "All depots" — two levels in place, ADR-080 §14) is a `Tree`: the
// box picks the whole group, the chevron only opens it, the children read
// "Included" while their parent is picked (pick-many — in pick-one a child
// replaces the whole); a group row HELD OPEN (`fixedOpen` — "All sites" over
// the followed: its kind is found by typing) has no chevron and never folds; a
// grouped list takes no virtual focus from the search box (the Tree consumes no
// Autocomplete — the host's hook filters), the box staying in its place. In the flat list an INCLUDED row (a typed depot while "All depots"
// is ticked) is drawn ticked and greyed, "Included" said with it. A non-pickable (REFUSED) row is greyed
// with its reason on its second line: in the flat list a disabled option (the
// foundation's default — nothing inside it to reach); a refused GROUP row stays
// reachable and unselectable (`disabledBehavior="selection"` — its chevron still
// opens the group). An inactive row is muted and says so in its description,
// read with the row.
// A row is never interactive inside (one row = one choice) — the group row's
// chevron is the foundation's own expand control. In pick-one mode a re-press
// of the chosen row emits an EMPTY selection and the HOST decides (a filter
// clears; a required field keeps its value — `Picker`). The search box
// (`PickerSearchBox`) is the family's, shared with the `Navigator`.
// ENTER PICKS THE FIRST RESULT (ADR-080 §11) — on every face. While the typed answer is still coming (the debounce,
// the fetch; the previous rows still on screen, the first of them holding the virtual focus) an Enter is HELD, never
// let through to a stale row: it picks the answer's first row when the answer lands; typing on, or Escape, drops it.

import { Fragment, useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import {
  Autocomplete,
  Button as AriaButton,
  Checkbox as AriaCheckbox,
  Header,
  Input as AriaInput,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  SearchField as AriaSearchField,
  Text,
  TextContext,
  Tree,
  TreeItem,
  TreeItemContent,
  type Key,
  type Selection,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import type { OptionRow } from '../../hooks/useOptions.js';
import { Button } from './Button.js';
import { icons } from './icons.js';

export interface PickerListProps {
  /** The rows as the source answered them — the groups first; a row with `children` is a GROUP row. */
  rows: readonly OptionRow[];
  /** The chosen values (pick-one: at most one). */
  value: readonly string[];
  onChange: (next: string[]) => void;
  multiple?: boolean;
  /** The chosen rows to LEAD the list under "Selected" — a long list whose host shows no chips (ADR-080 §6 · §10). */
  selected?: readonly OptionRow[];
  /** A search box above the rows (a list past 8, a server kind). */
  searchable?: boolean;
  /** The typed text — controlled: the host's hook matches on it. */
  text?: string;
  onTextChange?: (text: string) => void;
  searchPlaceholder?: string;
  /** Focus the search box on mount — a panel the person just opened; never in a drawer listing several. */
  searchAutoFocus?: boolean;
  /** The heading of a group key ("Followed" · …) — null = the group LEADS UNHEADED (its rows say it: the viewer's own "Me" row). Default: the family's words in `common:picker.groups`, `me` unheaded. */
  groupLabel?: (group: string) => string | null;
  // ── the state behind the rows (the hook's) — the notices and the spoken count ──
  loading?: boolean;
  searching?: boolean;
  failed?: boolean;
  hasMore?: boolean;
  settled?: boolean;
  /** The inactive rows the list hides at rest (a chosen one is already shown) — a short list offers "Show disabled (n)". */
  inactiveCount?: number;
  showInactive?: boolean;
  onShowInactiveChange?: (show: boolean) => void;
  /** The GROUP rows open at first render (the filter's drill opens the picked zone). */
  defaultExpanded?: readonly string[];
  /** The host's own word when nothing is listed and nothing is typed yet (a pool the typing searches — "Search for
   *  someone to add…"); the family's "Nothing to choose from" otherwise. */
  emptyLabel?: string;
  /** After a single pick — the host closes its panel. */
  onPick?: () => void;
  /** The list's LAST row, one that ACTS instead of picking ("Add a new product" — ADR-080 §15's shape): a press runs it
   *  and closes a pick-one host; it is never selected, never counted. */
  tail?: { label: string; icon?: ReactNode; onAction: () => void };
  /** The row's own look (the entity's face) — default: icon · label · description · trailing. */
  renderRow?: (row: OptionRow, state: { selected: boolean }) => ReactNode;
  ariaLabel: string;
  /** `touch` = finger-sized rows (the phone sheet). */
  rowSize?: 'default' | 'touch';
  className?: string;
}

/** A Tree row names itself through its `aria-label` (`treeName`) — its face's label / description slots are layout
 *  only. The row gives them their own context, so a host's (a popover's dialog) never claims them. */
const TREE_ROW_TEXT = { slots: { label: {}, description: {} } };
/** The acting last row's key — never a value. */
const TAIL_ID = '__tail__';
/** The foundation's own "typed forward: focus the first row" signal (react-aria's `FOCUS_EVENT`, which the search box sends
 *  its list on each keystroke — not a public export; the picker's browser test pins it). */
const FOCUS_FIRST = 'react-aria-focus';
const GROUP_HEADER = 'px-3 pt-2 pb-1 text-xs font-medium text-[var(--color-text-secondary)]';
const NOTICE = 'px-3 py-2 text-sm text-[var(--color-text-secondary)]';
// RAC drives `data-[focused]` from keyboard roving AND hover on a ListBox option; one rule covers both.
// Focus on a row = the row background, never an outline (DESIGN-TOKENS §Interaction states — the embedded
// chevron / arrow / clear buttons keep their inset ring). A REFUSED row (`data-refused`, from `pickable`) and an
// INCLUDED child (`data-included`) are greyed; in a FLAT list a refused row is the foundation's disabled option (the
// keyboard skips it), in the Tree and the GridList it stays reachable (`disabledBehavior="selection"`).
/** The row classes of the family's lists — shared with the `Navigator`. */
export const pickerRow = (touch: boolean) =>
  `flex items-center gap-2.5 rounded-md px-2.5 ${touch ? 'min-h-12 py-2.5 text-base' : 'min-h-9 py-1.5 text-sm'} cursor-pointer outline-none ` +
  'text-[var(--color-text-primary)] data-[focused]:bg-[var(--color-bg-hover)] data-[hovered]:bg-[var(--color-bg-hover)] data-[pressed]:bg-[var(--color-border)] ' +
  'data-[refused]:cursor-not-allowed data-[refused]:opacity-60 data-[included]:opacity-60';
const rowBase = pickerRow;

/** The family's search box — the one piece above every searched list (`PickerList`, the `Navigator`): the clear ✕ shows only with text. */
export function PickerSearchBox({ value, onChange, placeholder, focusAtOpen = false, touch = false }: { value: string; onChange: (text: string) => void; placeholder?: string; /** Focus the box on mount — the panel the person just opened; never in a drawer listing several. */ focusAtOpen?: boolean; touch?: boolean }) {
  const { t } = useTranslation('common');
  return (
    <AriaSearchField
      value={value}
      onChange={onChange}
      // eslint-disable-next-line jsx-a11y/no-autofocus -- in-panel safe-default focus: the host that just opened asks for it (never a drawer listing several)
      autoFocus={focusAtOpen}
      aria-label={placeholder ?? t('picker.search')}
      className="group mb-1 flex items-center gap-2 rounded-md field-frame px-2"
    >
      <icons.search aria-hidden size={14} className="shrink-0 text-[var(--color-text-secondary)]" />
      <AriaInput
        placeholder={placeholder ?? t('picker.search')}
        className={`search-own-clear min-w-0 flex-1 border-0 bg-transparent py-1.5 outline-none text-[var(--color-text-primary)] placeholder:text-[var(--color-text-disabled)] ${touch ? 'text-base' : 'text-sm pointer-coarse:text-base'}`}
      />
      <AriaButton aria-label={t('search.clear')} className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-[var(--color-text-secondary)] outline-none focus-ring-inset data-[hovered]:text-[var(--color-text-primary)] group-data-[empty]:invisible">
        <icons.close aria-hidden size={14} />
      </AriaButton>
    </AriaSearchField>
  );
}

/** The tick / the box: selection conveyed by the glyph, never colour alone. */
function Mark({ multiple, selected }: { multiple: boolean; selected: boolean }) {
  if (!multiple) return <icons.check aria-hidden size={16} weight="bold" className={`shrink-0 text-[var(--color-primary-display)] ${selected ? '' : 'invisible'}`} />;
  return (
    <span
      aria-hidden
      className={
        'flex size-4 shrink-0 items-center justify-center rounded border ' +
        (selected ? 'border-[var(--color-primary-display)] bg-[var(--color-primary-display)] text-[var(--color-on-primary-display)]' : 'border-[var(--color-border)]')
      }
    >
      {selected && <icons.check size={12} weight="bold" />}
    </span>
  );
}

/** The default face of a row: icon · label (+ the second line) · trailing (a badge) — the inactive word when nothing else says it. */
function RowFace({ row, inactiveWord }: { row: OptionRow; inactiveWord: string }) {
  // A badge's word and the inactive word ride the description READ with the row, drawn nowhere on it — the badge
  // beside the row (and the inactive word drawn there) is the eye's copy, hidden from a screen reader.
  const spoken = [row.spoken, row.inactive ? inactiveWord : null].filter(Boolean).join(' · ');
  return (
    <>
      {row.icon && (
        <span aria-hidden className="shrink-0 text-[var(--color-text-secondary)]">
          {row.icon}
        </span>
      )}
      <span className="min-w-0 flex-1">
        <Text slot="label" className={`block truncate ${row.inactive ? 'text-[var(--color-text-secondary)]' : ''}`}>
          {row.label}
        </Text>
        {(row.description || spoken) && (
          <Text slot="description" className="block truncate text-xs text-[var(--color-text-secondary)]">
            {row.description}
            {spoken && <span className="sr-only">{row.description ? `, ${spoken}` : spoken}</span>}
          </Text>
        )}
      </span>
      {row.trailing ? (
        <span aria-hidden className="shrink-0">
          {row.trailing}
        </span>
      ) : row.inactive ? (
        <span aria-hidden className="shrink-0 text-xs text-[var(--color-text-secondary)]">{inactiveWord}</span>
      ) : null}
    </>
  );
}

/** The family's row face for a host whose `renderRow` only reshapes the row (its own second line): the name and the
 *  second line stay the option's label and description slots — read with it, never folded into its name. */
export function PickerRowFace({ row }: { row: OptionRow }) {
  const { t } = useTranslation('common');
  return <RowFace row={row} inactiveWord={t('picker.inactive')} />;
}

export function PickerList({
  rows,
  value,
  onChange,
  multiple = false,
  selected,
  searchable = false,
  text = '',
  onTextChange,
  searchPlaceholder,
  searchAutoFocus = false,
  groupLabel,
  loading = false,
  searching = false,
  failed = false,
  hasMore = false,
  settled = true,
  inactiveCount = 0,
  showInactive = false,
  onShowInactiveChange,
  defaultExpanded,
  emptyLabel,
  onPick,
  tail,
  renderRow,
  ariaLabel,
  rowSize = 'default',
  className = '',
}: PickerListProps) {
  const { t } = useTranslation('common');
  const touch = rowSize === 'touch';
  const statusId = useId();
  const labelOfGroup = groupLabel ?? ((g: string) => (g === 'me' ? null : t(`picker.groups.${g}`, { defaultValue: g })));
  const typed = text.trim().length > 0;
  const grouped = rows.some((r) => r.children && r.children.length > 0);
  // The group rows open in place: those the host opens at first render, and those held open (no chevron, never folded).
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(() => new Set(defaultExpanded ?? []));
  const heldOpen = rows.filter((r) => r.fixedOpen && (r.children?.length ?? 0) > 0).map((r) => r.id);

  // THE START LIST of a long list (ADR-080 §6 — the reason beside the rule): a searched list is a moving window of
  // 20 rows on thousands, so the chosen need ONE stable place to be seen and undone — stable in both senses. The rows
  // chosen when the picker OPENED lead under "Selected" (a snapshot at mount — every host mounts the inside per open),
  // each shown once (the viewer's "Me" and an inactive pick too, marked), each named from the answer when it holds
  // the row (the richer face) and remembered once named; a row ticked while the picker is open stays in its place, a
  // lead row un-ticked stays on top until the next open. Typed, the list is the RESULTS in their order with the ticks
  // in place — nothing is lifted, so the first result is the one Enter picks (the foundation focuses it on typing).
  // A short list keeps its order with the ticks in place and leads only a chosen row it does not hold; a grouped
  // list (the Tree) keeps its rows in place; a host that passes no `selected` leads nothing.
  const [openedWith] = useState<readonly string[]>(() => [...new Set(value)]);
  // The lead rows as they get NAMED — remembered in state through the open session (a lead row un-ticked leaves
  // `selected`, its row stays); a row is taken from the answer when it holds it, once. Adjusted during render with
  // the previous-value guard (the house idiom, never an effect): an id is written at most twice — named, then answered.
  const [named, setNamed] = useState<ReadonlyMap<string, { row: OptionRow; answered: boolean }>>(() => new Map());
  let naming: Map<string, { row: OptionRow; answered: boolean }> | null = null;
  for (const s of selected ?? []) {
    const answered = rows.find((r) => r.id === s.id);
    const have = named.get(s.id);
    if (!have || (answered && !have.answered)) {
      naming ??= new Map(named);
      naming.set(s.id, { row: answered ?? s, answered: answered !== undefined });
    }
  }
  if (naming) setNamed(naming);
  const namedNow = naming ?? named;
  const away = (selected ?? []).filter((s) => !rows.some((r) => r.id === s.id));
  const lead: readonly OptionRow[] =
    selected === undefined || grouped ? [] : !searchable ? away : typed ? [] : openedWith.map((id) => namedNow.get(id)?.row).filter((r): r is OptionRow => r !== undefined);
  const leadIds = new Set(lead.map((r) => r.id));
  const body = leadIds.size === 0 ? rows : rows.filter((r) => !leadIds.has(r.id));
  // The rows by group, in the order the source answered — one section per RUN (the hook keeps a group's rows
  // contiguous; a run that recurs still draws its own section, keyed by position).
  const sections: { group: string | null; rows: OptionRow[] }[] = [];
  for (const r of body) {
    const last = sections[sections.length - 1];
    if (last && last.group === r.group) last.rows.push(r);
    else sections.push({ group: r.group, rows: [r] });
  }
  const total = body.length + lead.length;
  const disabledKeys = [...lead, ...body.flatMap((r) => [r, ...(r.children ?? [])])].filter((r) => !r.pickable || r.included).map((r) => r.id);

  // Pick-many: a picked GROUP replaces the children picked inside it — only the highest pick is stored (ADR-080 §8 · ADR-037 §2).
  const onSelection = (keys: Selection) => {
    if (keys === 'all') return;
    const next = [...keys].map(String);
    if (tail && next.includes(TAIL_ID)) {
      tail.onAction();
      if (!multiple) onPick?.();
      return;
    }
    const picked = multiple ? next.filter((id) => !rows.some((g) => next.includes(g.id) && (g.childIds ?? (g.children ?? []).map((c) => c.id)).includes(id))) : next.slice(0, 1);
    onChange(picked);
    if (!multiple) onPick?.();
  };

  // ── Enter held while the answer is still coming (the header's rule) ──
  const heldEnter = useRef(false);
  const onKeyDownCapture = (e: ReactKeyboardEvent) => {
    if (e.key !== 'Enter' || !searching) return;
    if (!(e.target instanceof HTMLInputElement)) return;
    // Stopped in the capture phase, so the foundation's input handler never relays it to the (stale) focused row.
    e.preventDefault();
    e.stopPropagation();
    heldEnter.current = true;
  };
  const onText = (v: string) => {
    heldEnter.current = false;
    onTextChange?.(v);
  };
  // The one side effect of the hold — the pick itself (the host's handler), once the answer has landed. It does what
  // the foundation's Enter does on a landed list: the first PICKABLE row (a refused row is skipped, and so is a row
  // included in a whole already picked — it is never picked on its own); pick-one on the row already chosen empties the
  // selection (the host decides — a filter clears); with no pickable row the tail runs.
  useEffect(() => {
    if (!heldEnter.current || searching || !settled) return;
    heldEnter.current = false;
    if (!typed) return;
    const first = rows.find((r) => r.pickable && !r.included);
    if (!first) {
      if (tail) onSelection(new Set<Key>([TAIL_ID]));
      return;
    }
    if (multiple) onSelection(new Set<Key>(value.includes(first.id) ? value.filter((v) => v !== first.id) : [...value, first.id]));
    else onSelection(new Set<Key>(value.includes(first.id) ? [] : [first.id]));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires when the answer settles; the pick reads what stands then
  }, [searching, settled]);

  // The notices — never an empty answer shown as nothing. An EMPTY list says its
  // word INSIDE the list (React Aria's empty state — reachable by the search box's
  // virtual focus, read as the list's one row): loading · searching · nothing
  // matches the typed text · nothing to choose from at all; a list with rows says
  // "more" (or a failure beside stale rows) under them — and the spoken count says
  // "more" too.
  const emptyWord = failed
    ? t('picker.failed')
    : loading
      ? t('picker.loading')
      : searching
        ? t('picker.searching')
        : typed && settled && rows.length === 0
          ? t('picker.noResults', { q: text.trim() })
          : !typed && settled && total === 0
            ? (emptyLabel ?? t('picker.empty'))
            : null;
  // With a tail row the collection is never empty — the empty word is said above the list instead; so is the loading
  // word while the rows on screen are the host's own (a filter's leading "No asset", a pinned suggestion) and the
  // source's start list is still loading.
  const renderEmptyState = emptyWord && !tail ? () => <p className={NOTICE}>{emptyWord}</p> : undefined;
  const above = tail && total === 0 && emptyWord ? emptyWord : loading && total > 0 ? t('picker.loading') : null;
  const emptyAbove = above ? <p className={NOTICE}>{above}</p> : null;
  const notices: ReactNode[] = [];
  if (total > 0 && failed) notices.push(<p key="failed" className={NOTICE}>{t('picker.failed')}</p>);
  if (!failed && hasMore) notices.push(<p key="more" className={NOTICE}>{typed ? t('picker.moreTyped') : t('picker.more')}</p>);
  const showInactiveDoor = !searchable && onShowInactiveChange && (inactiveCount > 0 || showInactive);

  const inactiveWord = t('picker.inactive');
  const face = (row: OptionRow, isSelected: boolean) => (renderRow ? renderRow(row, { selected: isSelected }) : <RowFace row={row} inactiveWord={inactiveWord} />);
  // ONE row of the flat list, wherever it sits (Selected · a group · the rest · plain). A row INCLUDED in a whole picked
  // above it (a typed depot while "All depots" is ticked) is drawn ticked and greyed, "Included" said with it — never
  // picked on its own (ADR-080 §8).
  const item = (row: OptionRow) => (
    <ListBoxItem
      key={row.id}
      id={row.id}
      textValue={row.selectedLabel ?? row.label}
      isDisabled={!row.pickable || row.included}
      data-refused={row.pickable ? undefined : ''}
      data-included={row.included ? '' : undefined}
      className={rowBase(touch)}
    >
      {({ isSelected }) => (
        <>
          <Mark multiple={multiple} selected={isSelected || row.included === true} />
          {face(row.included ? { ...row, spoken: [row.spoken, t('picker.included')].filter(Boolean).join(' · ') } : row, isSelected)}
          {row.included && <span aria-hidden className="shrink-0 text-xs text-[var(--color-text-secondary)]">{t('picker.included')}</span>}
        </>
      )}
    </ListBoxItem>
  );
  // A Tree row is named by its textValue alone — its description, the inactive word and "Included" ride an interpolated name.
  const treeName = (r: OptionRow, extra?: string) => [r.label, r.description, r.spoken, r.inactive ? inactiveWord : undefined, extra].filter(Boolean).join(', ');

  // ── the flat list: Autocomplete (the search box) + ListBox (the rows, in sections) ──
  // A TYPED answer re-mounts the list (one landing after the keystroke — the server's — would keep the search box's
  // active row on a row of the answer it replaced: a stale `aria-activedescendant`), and the new list is told what the
  // keystroke told the old one — typed forward, the FIRST row: the active row is the first result, the one Enter picks
  // (ADR-080 §11) — never a chosen row further down, which the foundation's own mount focus would prefer (its search
  // box asks a list that mounts AFTER a keystroke — the Location filter's kinds turning into results — to focus "first",
  // and that focus lands on a CHOSEN row: refused below, `autoFocus={false}`). An EMPTY typed answer keeps the list it
  // replaced: the list the keystroke told clears its active row itself once its rows go. Adjusted during render with
  // the previous-value guard (the house idiom).
  const liveKey = typed ? `typed:${body.map((r) => r.id).join('\u001f')}` : 'rest';
  const [answerKey, setAnswerKey] = useState(liveKey);
  const listKey = typed && body.length === 0 ? answerKey : liveKey;
  if (listKey !== answerKey) setAnswerKey(listKey);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (typed && searchable) listRef.current?.dispatchEvent(new CustomEvent(FOCUS_FIRST, { detail: { focusStrategy: 'first' } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mounted answer
  }, [listKey]);
  const flat = (
    <ListBox
      key={listKey}
      ref={listRef}
      // eslint-disable-next-line jsx-a11y/no-autofocus -- REFUSES the search box's mount focus (it prefers a chosen row); the first row is asked above
      autoFocus={false}
      aria-label={ariaLabel}
      aria-describedby={statusId}
      selectionMode={multiple ? 'multiple' : 'single'}
      // Escape never clears the chosen (the foundation's default would): it clears the typed text first, then closes the host.
      escapeKeyBehavior="none"
      selectedKeys={new Set(value as Key[])}
      onSelectionChange={onSelection}
      disabledKeys={disabledKeys}
      className="flex w-full flex-col gap-0.5 outline-none"
      renderEmptyState={renderEmptyState}
    >
      {lead.length > 0 && (
        <ListBoxSection id="__selected__">
          <Header className={GROUP_HEADER}>{t('picker.selected')}</Header>
          {lead.map(item)}
        </ListBoxSection>
      )}
      {sections.map((s, i) =>
        s.group !== null && labelOfGroup(s.group) !== null ? (
          <ListBoxSection key={`g:${s.group}:${i}`} id={`g:${s.group}:${i}`}>
            <Header className={GROUP_HEADER}>{labelOfGroup(s.group)}</Header>
            {s.rows.map(item)}
          </ListBoxSection>
        ) : s.group === null && (lead.length > 0 || sections.slice(0, i).some((x) => x.group !== null && labelOfGroup(x.group) !== null)) ? (
          // The REST of the list under a hairline once a heading precedes it (Selected · a group) — otherwise the rows
          // read as the last group's; named for assistive technology, never a printed heading (ADR-080 §6).
          <ListBoxSection key={`rest:${i}`} id={`__rest__:${i}`} aria-label={t('picker.rest')} className="mt-1 border-t border-[var(--color-border)] pt-1">
            {s.rows.map(item)}
          </ListBoxSection>
        ) : (
          <Fragment key={`p:${i}`}>{s.rows.map(item)}</Fragment>
        ),
      )}
      {tail && (
        <ListBoxItem id={TAIL_ID} textValue={tail.label} className={rowBase(touch)}>
          <Mark multiple={multiple} selected={false} />
          {tail.icon && (
            <span aria-hidden className="shrink-0 text-[var(--color-primary-display)]">
              {tail.icon}
            </span>
          )}
          <Text slot="label" className="block truncate font-medium text-[var(--color-primary-display)]">
            {tail.label}
          </Text>
        </ListBoxItem>
      )}
    </ListBox>
  );

  // ── the grouped list: a Tree — the box picks the whole group, the chevron only opens it; a group row held OPEN
  //    (`fixedOpen` — "All sites" over the followed) has no chevron and never folds ──
  const tree = (
    <Tree
      aria-label={ariaLabel}
      aria-describedby={statusId}
      selectionMode={multiple ? 'multiple' : 'single'}
      escapeKeyBehavior="none"
      // A refused group row is unselectable yet reachable — its chevron still opens it, its reason is read with it.
      disabledBehavior="selection"
      selectionBehavior="toggle"
      selectedKeys={new Set(value as Key[])}
      onSelectionChange={onSelection}
      expandedKeys={new Set<Key>([...expanded, ...heldOpen])}
      onExpandedChange={(keys) => setExpanded(new Set([...keys].map(String).filter((k) => !heldOpen.includes(k))))}
      // Pick-many: a picked group's children are "Included" (unselectable, their box drawn ticked); pick-one: a child replaces the whole.
      disabledKeys={[...disabledKeys, ...(multiple ? rows.filter((r) => value.includes(r.id)).flatMap((r) => (r.children ?? []).map((c) => c.id)) : [])]}
      className="flex w-full flex-col gap-0.5 outline-none"
      renderEmptyState={renderEmptyState}
    >
      {rows.map((row) => {
        const included = multiple && value.includes(row.id);
        const children = row.children ?? [];
        return (
          <TreeItem key={row.id} id={row.id} textValue={row.selectedLabel ?? row.label} aria-label={treeName(row)} hasChildItems={children.length > 0} data-refused={row.pickable ? undefined : ''} className={rowBase(touch)}>
            <TreeItemContent>
              {({ isSelected, isExpanded, hasChildItems }) => (
                <>
                  {multiple ? (
                    <AriaCheckbox slot="selection" aria-label={t('picker.select')} className="flex items-center outline-none">
                      <Mark multiple selected={isSelected} />
                    </AriaCheckbox>
                  ) : (
                    <Mark multiple={false} selected={isSelected} />
                  )}
                  <TextContext.Provider value={TREE_ROW_TEXT}>{face(row, isSelected)}</TextContext.Provider>
                  {hasChildItems && !row.fixedOpen && (
                    // The verb alone — the foundation appends the row's name. The caret points INTO the reading direction at rest (mirrored in RTL) and DOWN when open, in both directions.
                    <AriaButton slot="chevron" aria-label={isExpanded ? t('picker.collapse') : t('picker.expand')} className="ms-1 inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md outline-none focus-ring-inset data-[hovered]:bg-[var(--color-bg-hover)]">
                      <icons.caretRight aria-hidden size={14} className={`transition-transform rtl:-scale-x-100 ${isExpanded ? 'rotate-90 rtl:-rotate-90' : ''}`} />
                    </AriaButton>
                  )}
                </>
              )}
            </TreeItemContent>
            {children.map((child) => (
              <TreeItem key={child.id} id={child.id} textValue={child.selectedLabel ?? child.label} aria-label={treeName(child, included ? t('picker.included') : undefined)} data-refused={child.pickable ? undefined : ''} data-included={included ? '' : undefined} className={`${rowBase(touch)} ps-9`}>
                <TreeItemContent>
                  {({ isSelected }) => (
                    <>
                      {/* Included in the picked whole: a decorative ticked box — no checkbox to disagree with it. */}
                      {included ? (
                        <Mark multiple selected />
                      ) : multiple ? (
                        <AriaCheckbox slot="selection" aria-label={t('picker.select')} className="flex items-center outline-none">
                          <Mark multiple selected={isSelected} />
                        </AriaCheckbox>
                      ) : (
                        <Mark multiple={false} selected={isSelected} />
                      )}
                      <TextContext.Provider value={TREE_ROW_TEXT}>{face(child, isSelected)}</TextContext.Provider>
                      {included && <span aria-hidden className="shrink-0 text-xs text-[var(--color-text-secondary)]">{t('picker.included')}</span>}
                    </>
                  )}
                </TreeItemContent>
              </TreeItem>
            ))}
          </TreeItem>
        );
      })}
    </Tree>
  );

  const search = searchable ? <PickerSearchBox value={text} onChange={onText} placeholder={searchPlaceholder} focusAtOpen={searchAutoFocus} touch={touch} /> : null;

  const list = grouped ? tree : flat;

  return (
    <div className={`flex min-h-0 flex-1 flex-col ${className}`} onKeyDownCapture={onKeyDownCapture}>
      {/* The flat list rides Autocomplete (virtual focus from the search box). A grouped list (the Tree) does not consume
          it — the box stays plain for it — but sits in the same place, so a list that turns flat once typed (the Location
          filter's kinds → its results) never re-mounts the box under the hand. */}
      {searchable ? (
        <Autocomplete>
          {search}
          {emptyAbove}
          <div className="min-h-0 flex-1 overflow-y-auto">{list}</div>
        </Autocomplete>
      ) : (
        <>
          {search}
          {emptyAbove}
          <div className="min-h-0 flex-1 overflow-y-auto">{list}</div>
        </>
      )}
      {notices}
      {showInactiveDoor && (
        <div className="px-1 pt-1">
          <Button variant="link" onClick={() => onShowInactiveChange?.(!showInactive)}>
            {showInactive ? t('picker.hideInactive') : t('picker.showInactive', { count: inactiveCount })}
          </Button>
        </div>
      )}
      {/* The count, READ ALOUD ONLY (ADR-080 §2 — architect-ruled: nothing visible under the list). */}
      <div id={statusId} role="status" aria-live="polite" className="sr-only">
        {failed ? t('picker.failed') : loading || searching ? '' : hasMore ? t('picker.resultsMore', { count: total }) : t('picker.results', { count: total })}
      </div>
    </div>
  );
}
